import React, { MouseEventHandler, useCallback } from 'react';

import { useAtom } from 'jotai/index';
import { useLocation } from 'react-router-dom';

import { cookieJar, uiRoutes } from '@consumer-frontend/api';
import { resetAnalytics } from '@consumer-frontend/shared';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  ListGroupItem,
  NavLink,
} from '@consumer-frontend/shared-ui';

import { showMobileNavAtom } from '../../store';

import '@consumer-bootstrap/index.scss';
import '@consumer-bootstrap/pages/account-home/nav.scss';

export type NoLoanNavT = React.FC;

export const NoLoanNav: NoLoanNavT = () => {
  const [, setShowMobileNav] = useAtom(showMobileNavAtom);
  const { pathname } = useLocation();

  const isMessageCenterActive = pathname.startsWith(uiRoutes.messages) || pathname.startsWith(uiRoutes.messageDetail);

  const closeMobileNav: MouseEventHandler = useCallback(
    (ev) => {
      (ev?.currentTarget as HTMLElement)?.blur();

      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  const handleSignout = useCallback(
    () => {
      cookieJar.expire();
      resetAnalytics();
      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  return (
    <>
      <ListGroupItem>
        <NavLink to={uiRoutes.accountHome} onClick={closeMobileNav}>
          <Icon iconName="house-door" />
          <span data-text="Account Home">Account Home</span>
        </NavLink>
      </ListGroupItem>
      <ListGroupItem className="border-bottom-0 pb-0">
        <Dropdown autoClose={false}>
          <DropdownToggle as="button" className="nav-link">
            <Icon iconName="file-ruled" />
            <span className="text-start text-wrap">Statements & Activity</span>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.accountActivity}
              onClick={closeMobileNav}
            >
              <span>Account Activity</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.accountHistory}
              onClick={closeMobileNav}
            >
              <span>Account History</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </ListGroupItem>
      <ListGroupItem className="border-bottom-0 py-0">
        <Dropdown autoClose={false}>
          <DropdownToggle as="button" className="nav-link py-0">
            <Icon iconName="currency-dollar" />
            <span>Payments</span>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.paymentMethods}
              onClick={closeMobileNav}
            >
              <span>Payment Methods</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </ListGroupItem>
      <ListGroupItem className="pt-0">
        <Dropdown autoClose={false}>
          <DropdownToggle as="button" className="nav-link">
            <Icon iconName="person" />
            <span>Profile</span>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.personal}
              onClick={closeMobileNav}
            >
              <span>Personal</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.employment}
              onClick={closeMobileNav}
            >
              <span>Employment</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.contact}
              onClick={closeMobileNav}
            >
              <span>Contact Information</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.password}
              onClick={closeMobileNav}
            >
              <span>Change Password</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.documents}
              onClick={closeMobileNav}
            >
              <span>Documents</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </ListGroupItem>
      <ListGroupItem>
        <NavLink to={uiRoutes.messages} onClick={closeMobileNav} active={isMessageCenterActive}>
          <Icon iconName="envelope" />
          <span>Message Center</span>
        </NavLink>
        <NavLink to={uiRoutes.contactUs} onClick={closeMobileNav}>
          <Icon iconName="chat-left" />
          <span>Contact Us</span>
        </NavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NavLink to={uiRoutes.products} onClick={closeMobileNav}>
          <span>Products</span>
        </NavLink>
        <NavLink to={uiRoutes.ratesAndTerms} onClick={closeMobileNav}>
          <span>Rates and Terms</span>
        </NavLink>
        <NavLink to={uiRoutes.faq} onClick={closeMobileNav}>
          <span>FAQs</span>
        </NavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NavLink to={uiRoutes.logOut} onClick={handleSignout}>
          <Icon iconName="box-arrow-right" />
          <span>Sign Out</span>
        </NavLink>
      </ListGroupItem>
    </>
  );
};
